// Floating clone that holds `card-<eid>` as the Motion layoutId for
// the hover beat. The matching hand slot suppresses its own layoutId
// (via overrides.suppress) so Motion only sees one element with that
// id at a time. Mid-fizzle the clone runs the cyan dissolve variant
// and drops its layoutId so Motion doesn't try to FLIP it into the
// graveyard ghost — the spell visibly dissolves in place.

import { motion } from "motion/react";
import { Card } from "../Card";
import type { pixelTheme } from "../../theme/pixel";
import type { PlayClone } from "../../logic/useBattleAnimations";
import {
  spellFizzleTransition,
  spellFizzleVariants,
  zoneMoveTransition,
} from "../../logic/motion";

export function PlayCloneCard({
  playClone,
  theme,
  centralRect,
}: {
  playClone: PlayClone;
  theme: typeof pixelTheme;
  centralRect: DOMRect | null;
}) {
  const { fizzling } = playClone;
  // Clip the clone to the central play area so the dissolve / pop
  // animations can't paint outside it.
  const clip = centralRect
    ? clipInsetForRect(playClone.rect, centralRect)
    : null;
  const layoutId = fizzling ? undefined : `card-${playClone.eid}`;
  return (
    <motion.div
      layoutId={layoutId}
      initial={spellFizzleVariants.initial}
      animate={fizzling ? spellFizzleVariants.fizzling : spellFizzleVariants.initial}
      transition={fizzling ? spellFizzleTransition() : zoneMoveTransition()}
      style={{
        position: "fixed",
        left: playClone.rect.left,
        top: playClone.rect.top,
        width: playClone.rect.width,
        height: playClone.rect.height,
        zIndex: 1000,
        pointerEvents: "none",
        ...(clip ? { clipPath: clip } : null),
      }}
    >
      <Card card={playClone.card} size="normal" theme={theme} />
    </motion.div>
  );
}

// 1px motion.span pinned at the opp-hand anchor's geometric centre,
// holding `card-<eid>` so Motion has a destination rect to FLIP a
// leaving field tile to when it returns to the opponent's hidden hand.
// Unmounts itself when its layout animation resolves.
export function BounceAnchorGhost({
  eid,
  onComplete,
}: {
  eid: number;
  onComplete: (eid: number) => void;
}) {
  return (
    <motion.span
      layoutId={`card-${eid}`}
      transition={zoneMoveTransition()}
      onLayoutAnimationComplete={() => onComplete(eid)}
      aria-hidden="true"
      style={{
        position: "absolute",
        top: "50%",
        left: "50%",
        width: 1,
        height: 1,
        opacity: 0,
        pointerEvents: "none",
      }}
    />
  );
}

function clipInsetForRect(child: DOMRect, container: DOMRect): string {
  const top = Math.max(0, container.top - child.top);
  const right = Math.max(0, child.right - container.right);
  const bottom = Math.max(0, child.bottom - container.bottom);
  const left = Math.max(0, container.left - child.left);
  return `inset(${top}px ${right}px ${bottom}px ${left}px)`;
}
